/**
 * The beam.
 *
 * Fanar means lighthouse, and this is the light: the exact patch of road the alert
 * filter is watching. It is not decoration — every edge is a real parameter.
 *   - it opens at the bearing cone the filter uses (45 deg at speed, 75 deg slow)
 *   - it straightens once the cone reaches the 150 m cross-track corridor
 *   - it ends at the lookahead distance, which is 45 seconds of travel
 * So it lengthens as you accelerate and pulls in as you slow, and a driver can see
 * why a hazard did or did not speak.
 */
import { destination, lookaheadMetres, type Point } from "../../shared/geo.js";
import type { Fix } from "../../shared/alerts.js";

/** Matches the cross-track test in `isAhead`. */
const CORRIDOR_HALF_WIDTH_M = 150;

type Ring = [number, number][];

const EMPTY: { type: "FeatureCollection"; features: never[] } = { type: "FeatureCollection", features: [] };

export type BeamData = {
  type: "FeatureCollection";
  features: { type: "Feature"; properties: Record<string, never>; geometry: { type: "Polygon"; coordinates: Ring[] } }[];
};

const xy = (p: Point): [number, number] => [p.lon, p.lat];

export function beamPolygon(fix: Fix): BeamData | typeof EMPTY {
  // With no heading there is no "ahead", and drawing a cone would be a lie.
  if (fix.course === null || !Number.isFinite(fix.course)) return EMPTY;

  const me: Point = { lat: fix.lat, lon: fix.lon };
  const course = fix.course;
  const coneDeg = fix.speedMps > 12 ? 45 : 75;
  const lookahead = lookaheadMetres(fix.speedMps);

  // Distance at which the opening cone reaches the corridor's full width.
  const flare = Math.min(lookahead, CORRIDOR_HALF_WIDTH_M / Math.sin((coneDeg * Math.PI) / 180));

  const ring: Ring = [xy(me), xy(destination(me, course + coneDeg, flare))];

  if (lookahead > flare) {
    const far = destination(me, course, lookahead);
    ring.push(xy(destination(far, course + 90, CORRIDOR_HALF_WIDTH_M)));
    ring.push(xy(destination(far, course - 90, CORRIDOR_HALF_WIDTH_M)));
  }

  ring.push(xy(destination(me, course - coneDeg, flare)));
  ring.push(xy(me));

  return {
    type: "FeatureCollection",
    features: [{ type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [ring] } }],
  };
}
